import * as yup from "yup";

import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { FaCircleNotch } from "react-icons/fa";
import { IoSaveOutline } from "react-icons/io5";
import { useState, useEffect } from "react";

import {
  MULTIMEDIA_PLACEHOLDER,
  MULTIMEDIA_MIME_TYPES,
  FILE_ALREADY_EXISTS,
} from "../../../utils/constants";

import Datepicker from "../../../components/common/Datepicker";
import FileUpload from "../../../components/common/FileUpload";
import Textbox from "../../../components/common/Textbox";
import Toaster from "../../../components/common/Toaster";

import complianceService from "../../../services/complianceService";
import moment from "moment";

const S2LicenseForm = ({ data, account, setShowForm, onSuccess }) => {
  const schema = yup.object({
    id: yup.string(),
    number: yup.string().required("Please enter S2 License No."),
    validity: yup.string().required("Please select validity."),
    registration_date: yup
      .string()
      .required("Please select registration date."),
    files: yup
      .mixed()
      .test(
        "is-valid-file-name",
        "A string in a file name must match with the first name.",
        (value) => isValidFileName(value)
      ),
  });

  const [fileExists, setFileExists] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const [registrationDate, setRegistrationDate] = useState(null);
  const [validity, setValidity] = useState(null);

  const [dateToday] = useState(
    Date.parse(moment(new Date(), "MM-DD-yyyy").toISOString())
  );

  const {
    register,
    handleSubmit,
    setValue,
    clearErrors,
    formState: { errors },
  } = useForm({ resolver: yupResolver(schema) });

  const onSubmit = async (info) => {
    try {
      setIsLoading(true);

      const s2_license = { ...info };
      s2_license["account_id"] = account.id;

      const result = await complianceService.saveS2LicenseAsync(s2_license);

      const item = { ...s2_license };
      item["id"] = result.id;
      item["validity"] = moment(info.validity).format("LL");
      item["registration_date"] = moment(info.registration_date).format("LL");

      if (result.s3_reference_names && result.s3_reference_names.length > 0) {
        item["s3_reference_name"] = result.s3_reference_names[0];
        item["document_name"] = result.files[0].file_name;
      }

      Toaster.Success("S2 License Saved Successfully");
      setShowForm(false);
      onSuccess(item);

      setIsLoading(false);
    } catch (ex) {
      if (ex.response && ex.response.data.message === FILE_ALREADY_EXISTS) {
        setFileExists(true);
        setIsLoading(false);
        return;
      }

      Toaster.Error("Something went wrong.");
      setIsLoading(false);
    }
  };

  const isValidFileName = (file) => {
    if (file === undefined) return true;

    let first_name = account.first_name;
    let file_name = file[0].name;

    first_name = first_name.toLowerCase();
    file_name = file_name.toLowerCase();

    return file_name.includes(first_name);
  };

  useEffect(() => {
    if (data) {
      const registration = moment(data.registration_date).format("MM-DD-yyyy");
      const validity = moment(data.validity).format("MM-DD-yyyy");

      setValue("id", data.id);
      setValue("number", data.number);
      setValue("registration_date", registration);
      setValue("validity", validity);

      setRegistrationDate(registration);
      setValidity(validity);
    }
  }, [data, setValue]);

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="p-8 space-y-6">
      <div className="grid grid-cols-2 gap-6">
        <div className="col-span-2">
          <Textbox
            id="number-textbox"
            name="number"
            type="text"
            title="Number"
            register={register("number")}
            errors={errors}
          />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-6">
        <div className="col-span-2">
          <Datepicker
            id="registration-date-datepicker"
            name="registration_date"
            title="Registration Date"
            dateFormat="MM-DD-yyyy"
            initialValue={dateToday}
            value={registrationDate}
            timeFormat={false}
            setValue={setValue}
            clearErrors={clearErrors}
            register={register("registration_date")}
            onDateChange={(date) => setRegistrationDate(date)}
            errors={errors}
          />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-6">
        <div className="col-span-2">
          <Datepicker
            id="validity-date-datepicker"
            name="validity"
            title="Validity"
            dateFormat="MM-DD-yyyy"
            initialValue={dateToday}
            value={validity}
            timeFormat={false}
            setValue={setValue}
            clearErrors={clearErrors}
            register={register("validity")}
            onDateChange={(date) => setValidity(date)}
            errors={errors}
          />
        </div>
      </div>
      {!data && (
        <div className="grid grid-cols-2">
          <div className="col-span-2">
            <FileUpload
              id="files-dropdown"
              name="files"
              title="Files"
              setValue={setValue}
              clearErrors={clearErrors}
              register={register("files")}
              errors={errors}
              accepts={MULTIMEDIA_MIME_TYPES}
              placeholder={MULTIMEDIA_PLACEHOLDER}
              maxFiles={1}
              maxSize={5242880}
              multiple={false}
            />
            {fileExists && (
              <div className="pt-2">
                <span className="text-sm text-red-400">
                  {FILE_ALREADY_EXISTS}
                </span>
              </div>
            )}
          </div>
        </div>
      )}
      <div className="grid grid-cols-2 pt-2">
        <div className="col-span-2">
          <button
            type="submit"
            className="button button-primary disabled:opacity-70 disabled:cursor-not-allowed"
            disabled={isLoading}
          >
            {isLoading && <FaCircleNotch className="animate-spin mr-3" />}
            {!isLoading && <IoSaveOutline className="mr-3" />}
            Save Changes
          </button>
        </div>
      </div>
    </form>
  );
};

export default S2LicenseForm;
